import { Chat } from "@/lib/vibrato-api"
import { Clock, MessageText, ArrowRight } from "iconoir-react"
import { LoadingSpinner } from "../LoadingSpinner"

interface ChatHistoryProps {
  chats: Chat[]
  loading: boolean
  onChatSelect: (chatId: number) => void
  onNewChat: () => void
}

function formatTimeAgo(dateString: string): string {
  const now = new Date()
  const date = new Date(dateString)
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return "Just now"
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) return `${diffInDays}d ago`

  const diffInMonths = Math.floor(diffInDays / 30)
  return `${diffInMonths}mo ago`
}

function ChatCard({ chat, onClick }: { chat: Chat; onClick: () => void }) {
  const displayText = chat.summary || "New conversation"
  const truncatedText = displayText.length > 60 ? `${displayText.substring(0, 60)}...` : displayText

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer group"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="p-2 bg-purple-100 rounded-lg shrink-0">
              <MessageText className="w-4 h-4 text-purple-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-purple-600 font-medium text-sm line-clamp-2 leading-snug mb-1">{truncatedText}</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{formatTimeAgo(chat.updated_at)}</span>
              </div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors shrink-0" />
        </div>
      </div>
    </div>
  )
}

function EmptyState({ onNewChat }: { onNewChat: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 bg-white rounded-2xl shadow-md">
      <div className="p-4 bg-purple-100 rounded-xl mb-6">
        <MessageText className="w-8 h-8 text-purple-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-3">No previous chats</h2>
      <p className="text-gray-600 text-center max-w-lg mb-8">
        Start your first conversation with our AI assistant to get help with any task.
      </p>
      <button
        onClick={onNewChat}
        className="px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
      >
        <MessageText className="w-5 h-5" />
        Start New Chat
      </button>
    </div>
  )
}

export default function ChatHistory({ chats, loading, onChatSelect, onNewChat }: ChatHistoryProps) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 bg-white rounded-2xl shadow-md">
        <LoadingSpinner className="w-8 h-8 text-purple-600 mb-4" />
        <p className="text-gray-600">Loading chat history...</p>
      </div>
    )
  }

  if (chats.length === 0) {
    return <EmptyState onNewChat={onNewChat} />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Chat History</h2>
        <button
          onClick={onNewChat}
          className="px-4 py-2 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 text-sm"
        >
          <MessageText className="w-4 h-4" />
          New Chat
        </button>
      </div>

      <div className="space-y-3">
        {chats.map((chat) => (
          <ChatCard key={chat.id} chat={chat} onClick={() => onChatSelect(chat.id)} />
        ))}
      </div>

      {chats.length >= 50 && (
        <div className="text-center pt-4">
          <p className="text-sm text-gray-500">Showing most recent 50 chats</p>
        </div>
      )}
    </div>
  )
}
